import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import AppSidebar, { MobileTopBar } from "@/components/AppSidebar";
import { CheckCircle2, Circle } from "lucide-react";
import { wednesdayNavItems } from "@/pages/WednesdayNights";
import { GRUDEM_PARTS, TOTAL_CHAPTERS } from "@/lib/grudem";

export default function WednesdayProgress({ user }) {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const navItems = wednesdayNavItems(navigate, user, "progress");

  useEffect(() => {
    axios.get(`${API}/wednesday`).then((res) => setSessions(res.data)).finally(() => setLoading(false));
  }, []);

  // Map chapter number -> most recent published session covering it
  const chapterSessionMap = {};
  sessions
    .filter((s) => s.is_published && s.chapter_number)
    .forEach((s) => {
      if (!chapterSessionMap[s.chapter_number] || s.week_number > chapterSessionMap[s.chapter_number].week_number) {
        chapterSessionMap[s.chapter_number] = s;
      }
    });
  const coveredChapters = Object.keys(chapterSessionMap).map(Number);
  const coveredCount = coveredChapters.length;
  const currentChapter = coveredCount > 0 ? Math.max(...coveredChapters) : null;
  const pct = Math.round((coveredCount / TOTAL_CHAPTERS) * 100);

  return (
    <div className="min-h-screen bg-slate-50">
      <AppSidebar user={user} active="wednesday" brandLabel="Wednesday Nights" navItems={navItems} />
      <MobileTopBar brandLabel="Wednesday Nights" />
      <div className="md:ml-[68px] xl:ml-[260px] pt-14 md:pt-0">
        <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Book Progress</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Our journey through Wayne Grudem's <span className="italic">Systematic Theology</span>.
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-500 rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Overall progress */}
              <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="progress-overview">
                <div className="flex items-end justify-between mb-3">
                  <div>
                    <p className="text-3xl font-bold text-indigo-600">{pct}%</p>
                    <p className="text-xs text-slate-500 mt-0.5">{coveredCount} of {TOTAL_CHAPTERS} chapters covered</p>
                  </div>
                  {currentChapter && (
                    <p className="text-xs font-semibold text-slate-500 text-right">
                      Currently on<br />
                      <span className="text-indigo-600 text-sm">Chapter {currentChapter}</span>
                    </p>
                  )}
                </div>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>

              {/* Parts */}
              <div className="space-y-4" data-testid="progress-parts-list">
                {GRUDEM_PARTS.map((part) => {
                  const partCovered = part.chapters.filter((c) => chapterSessionMap[c.n]).length;
                  return (
                    <div key={part.part} className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                      <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between gap-3">
                        <div>
                          <p className="text-xs font-bold uppercase tracking-wider text-indigo-500">Part {part.part}</p>
                          <p className="text-sm font-semibold text-slate-800">{part.title}</p>
                        </div>
                        <span className="text-xs font-medium text-slate-400 shrink-0">
                          {partCovered}/{part.chapters.length}
                        </span>
                      </div>
                      <div className="divide-y divide-slate-50">
                        {part.chapters.map((ch) => {
                          const covered = chapterSessionMap[ch.n];
                          const isCurrent = ch.n === currentChapter;
                          const Row = covered ? "button" : "div";
                          return (
                            <Row
                              key={ch.n}
                              data-testid={`progress-chapter-${ch.n}`}
                              {...(covered ? { onClick: () => navigate(`/wednesday?session_id=${covered.session_id}`) } : {})}
                              className={`flex items-center gap-3 px-5 py-2.5 w-full text-left ${
                                covered ? "hover:bg-indigo-50/50 cursor-pointer transition-colors" : ""
                              } ${isCurrent ? "bg-indigo-50/70" : ""}`}
                            >
                              {covered ? (
                                <CheckCircle2 className="w-4 h-4 text-indigo-500 shrink-0" />
                              ) : (
                                <Circle className="w-4 h-4 text-slate-200 shrink-0" />
                              )}
                              <span className={`text-xs font-bold w-6 shrink-0 ${covered ? "text-indigo-600" : "text-slate-300"}`}>
                                {ch.n}
                              </span>
                              <span className={`text-sm flex-1 min-w-0 truncate ${covered ? "text-slate-700" : "text-slate-400"}`}>
                                {ch.title}
                              </span>
                              {isCurrent && (
                                <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-600 bg-indigo-100 rounded-full px-2 py-0.5 shrink-0">
                                  Current
                                </span>
                              )}
                              {covered && !isCurrent && (
                                <span className="text-[10px] text-slate-400 shrink-0">Wk {covered.week_number}</span>
                              )}
                            </Row>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
